import { DealModerationCardEntity } from 'src/entities/deal';
import { useEffect, useMemo } from 'react';
import { DealProcessingEvents, DealProcessingStatus, ProcessingViewModel } from '@common/model/processing';
import { coreDealApi } from '@cp/entities/deal';
import { Skeleton } from '@cp/ds/src/components/skeleton';
import { adminCatalogFeedbackApi } from 'src/entities/catalog/feedback';
import { DealStatus } from '@common/model/deal';
import { ClientType } from '@common/model/client/clientType';
import { Typography } from '@mui/material';
import { useProcessingAndReviews } from '../processingAndReviews';
import { useProcessingSocket } from 'src/entities/processing';
import { ProcessingFooter } from './processingFooter';
import { StatisticsView } from './statisticsView';
import { ClickFeature } from '.';

interface ProcessingItemProps {
  onClick: ClickFeature;
  processing: ProcessingViewModel;
}

export const ProcessingItem = (props: ProcessingItemProps) => {
  const socket = useProcessingSocket();
  const { form } = useProcessingAndReviews();
  const deal = coreDealApi.endpoints.getOne.useQuery({ dealId: props.processing.dealId });
  const isSpecialistInitiator = deal.data?.initiatorClientId === deal.data?.subjectProviderClientId;
  const feedbacks = adminCatalogFeedbackApi.endpoints.adminFeedbacks.useQuery(
    { dealId: props.processing.dealId },
    {
      skip: deal.data?.status !== DealStatus.Completed,
    },
  );

  const isProcessed = props.processing.status === DealProcessingStatus.Processed;

  const foot = useMemo(() => {
    const footAtWorkStatuses: number[] = [DealStatus.Negotiation, DealStatus.Rejected, DealStatus.Completed];
    if (
      !deal.data ||
      isProcessed ||
      !footAtWorkStatuses.includes(deal.data?.status || 0) ||
      (feedbacks.data && feedbacks.data.totalCount > 1)
    ) {
      return null;
    }

    return (
      <ProcessingFooter deal={deal.data} processing={props.processing}>
        <Typography component="cite" variant="caption">
          {deal.data?.rejectionReasonDescription}
        </Typography>
      </ProcessingFooter>
    );
  }, [deal.data, isProcessed, feedbacks.data, props.processing]);

  const fromSlot = useMemo(() => {
    if (!deal.data || !deal.data.initiatorClientId) return null;
    const clientType = isSpecialistInitiator ? ClientType.Specialist : ClientType.Employer;
    return <StatisticsView clientType={clientType} participantId={deal.data.initiatorClientId} status={deal.data.status} />;
  }, [deal.data, isSpecialistInitiator]);

  const toSlot = useMemo(() => {
    if (!deal.data || !deal.data.initiatorClientId) return null;
    const clientType = isSpecialistInitiator ? ClientType.Employer : ClientType.Specialist;
    const participantId = isSpecialistInitiator ? deal.data.subjectConsumerClientId : deal.data.subjectProviderClientId;
    return <StatisticsView clientType={clientType} participantId={participantId} status={deal.data.status} />;
  }, [deal.data, isSpecialistInitiator]);

  useEffect(() => {
    const handleUpdate = (ids: string[]) => {
      if (!deal.isUninitialized && ids.includes(props.processing.id)) deal.refetch();
      if (!feedbacks.isUninitialized && ids.includes(props.processing.id)) feedbacks.refetch();
    };
    socket.on(DealProcessingEvents.Updated, handleUpdate);
    return () => {
      socket.off(DealProcessingEvents.Updated, handleUpdate);
    };
  }, [deal, feedbacks, props.processing.id, socket]);

  if (!deal.data || deal.isLoading || feedbacks.isLoading) {
    return <Skeleton height={120} sx={{ transform: 'scale(1)', borderRadius: 2 }} width="100%" />;
  }

  const createdAt = new Date(deal.data.createdAt);
  const careerType = form.getValues('careerId');
  const initiator = isSpecialistInitiator ? deal.data.subjectProvider : deal.data.subjectConsumer;
  const executer = isSpecialistInitiator ? deal.data.subjectConsumer : deal.data.subjectProvider;
  const to = `${executer?.details.name} ${executer?.details.surname ? executer?.details.surname[0] + '.' : ''}`.trim();
  const from = `${initiator?.details.name} ${!!initiator?.details.surname ? initiator?.details.surname[0] + '.' : ''}`.trim();

  if (!initiator || !executer) {
    return null;
  }

  return (
    <DealModerationCardEntity
      careerType={careerType}
      changeTypeOfClients={isSpecialistInitiator}
      createdAt={createdAt}
      enableDim={isProcessed}
      from={from}
      onClick={props.onClick(deal.data)}
      slots={{ foot, from: fromSlot, to: toSlot }}
      to={to}
    />
  );
};

export const withRenderProcessingItem = (onClick: ClickFeature) => (processing: ProcessingViewModel) => (
  <ProcessingItem key={processing.id} onClick={onClick} processing={processing} />
);
